import { Epic, ofType } from 'redux-observable';
import { concat, EMPTY, from, merge, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap } from 'rxjs/operators';

import { Action } from '../actions';
import {
    ACCEPT_CAMPAIGN_AND_GROUP_SELECTOR_DATA,
    ACCEPT_CAMPAIGN_AND_GROUP_SELECTOR_DATA_DNA,
    changeCampaignsAndGroupSelectorStatus,
    FETCH_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGNS_DATA,
    FETCH_CAMPAIGN_AND_GROUP_SELECTOR_GROUPS_DATA,
    fetchCampaignAndGroupSelectorCampaignsData,
    FetchCampaignAndGroupSelectorCampaignsData,
    fetchCampaignAndGroupSelectorGroupsData,
    FetchCampaignAndGroupSelectorGroupsData,
    OPEN_CAMPAIGN_AND_GROUP_SELECTOR,
    OpenCampaignAndGroupSelector,
    openCampaignAndGroupSelector,
    openCampaignTypesMenu,
    SELECT_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGN,
    SelectCampaignAndGroupSelectorCampaign,
    selectCampaignAndGroupSelectorCampaign,
    selectCampaignAndGroupSelectorGroup,
    START_ADD_ENTITY_FROM_MENU,
    StartAddEntityFromMenu,
    updateCampaignAndGroupSelectorCampaignsData,
    updateCampaignAndGroupSelectorGroupsData
} from '../actions/campaignAndGroupSelector';
import { closeSidebarMenus } from '../actions/common';
import {
    addEntity,
    goToBannersEditForCreation,
    GoToBannersEditForCreationAction,
    goToGroupsEditForCreation,
    GoToGroupsEditForCreationAction,
    goToInternalBannersEditForCreation,
    GoToInternalBannersEditForCreationAction
} from '../actions/grid-screen';
import {
    createMcBannerAdGroup,
    CreateMcBannerAdGroupAction,
    createTextAdGroup,
    CreateTextAdGroupAction
} from '../actions/groupsEdit';
import { phrasesSelectorGetKeywords, phrasesSelectorOpenEditor } from '../actions/phrases-selector';
import { openPhrasesSelectorWizard } from '../actions/phrasesSelectorWizard';
import { SelectorStatus } from '../components/CampaignAndGroupSelector/CampaignAndGroupSelector';
import { SelectorItems } from '../components/CampaignAndGroupSelector/Selector/Selector';
import { getNewGroupName } from '../mappers/group/group';
import { selectGroupsSuggestListFilter } from '../selectors/campaignAndGroupSelector';
import { selectPreselectedCampaign, selectPreselectedGroup } from '../selectors/grid/grid';
import {
    selectAddableGroupTypes,
    selectCampaignTypesForBannerCreation,
    selectClientFeatures
} from '../selectors/groupEdit/group/group';
import { getCampaignAndGroupSelectorAvailableCampaignTypes } from '../service/CampaignAndGroupSelectorHelpers';
import '../service/Rx';
import { EpicDeps } from '../service/StoreFactory';
import { CampaignType, GroupType } from '../typings/domain';
import { DnaEpic, DnaEpicAction } from '../typings/epic';
import { GdCampaignTypeInput } from '../typings/graphql-grid';
import { GridEditedEntityType } from '../typings/grid';
import { PhrasesSelectorWizardId } from '../typings/phrases';
import { CampaignAndGroupSelectorEntityToSelectType } from '../typings/sidebar';
import { State } from '../typings/state';

const fetchCampaignAndGroupSelectorCampaignsDataEpic: DnaEpic = (action$, store$, deps) => {
    const requestAction$ = action$.pipe(
        ofType(FETCH_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGNS_DATA),
        debounceTime(500),
        mergeMap(
            (action: FetchCampaignAndGroupSelectorCampaignsData): DnaEpicAction => {
                const {
                    commonData: { login, accessRights }
                } = store$.value;

                let typeIn = getCampaignAndGroupSelectorAvailableCampaignTypes(action.entityToSelectType);

                if (accessRights.canViewInternalAds) {
                    typeIn = [GdCampaignTypeInput.INTERNAL_DISTRIB, GdCampaignTypeInput.INTERNAL_FREE];
                }

                return from(deps.entitiesDataProvider.fetchCampaignsSuggestList(login, action.filter, typeIn)).pipe(
                    mergeMap(
                        (data: SelectorItems[]): DnaEpicAction => {
                            const preselectedCampaign = selectPreselectedCampaign(store$.value, data);

                            return concat(
                                of(updateCampaignAndGroupSelectorCampaignsData(data)),
                                of(selectCampaignAndGroupSelectorCampaign(preselectedCampaign)),
                                of(changeCampaignsAndGroupSelectorStatus(SelectorStatus.READY))
                            );
                        }
                    ),
                    catchError(() => of(changeCampaignsAndGroupSelectorStatus(SelectorStatus.CAMPAIGN_ERROR)))
                );
            }
        )
    );

    const resetAction$ = action$.pipe(
        ofType(FETCH_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGNS_DATA),
        filter(() => {
            const {
                grid: { campaignAndGroupSelector }
            } = store$.value;
            const { selectorStatus } = campaignAndGroupSelector;

            return selectorStatus !== SelectorStatus.LOADING_CAMPAIGN;
        }),
        mergeMap(
            (action: FetchCampaignAndGroupSelectorCampaignsData): DnaEpicAction => {
                return merge(
                    of(updateCampaignAndGroupSelectorCampaignsData([])),
                    of(selectCampaignAndGroupSelectorCampaign(undefined)),
                    of(updateCampaignAndGroupSelectorGroupsData([])),
                    of(changeCampaignsAndGroupSelectorStatus(SelectorStatus.LOADING_CAMPAIGN)),
                    of(selectCampaignAndGroupSelectorGroup(undefined))
                );
            }
        )
    );

    return merge(resetAction$, requestAction$);
};

const fetchCampaignAndGroupSelectorGroupsDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    const requestAction$ = action$
        .ofType(FETCH_CAMPAIGN_AND_GROUP_SELECTOR_GROUPS_DATA)
        .debounceTime(500)
        .mergeMap((action: FetchCampaignAndGroupSelectorGroupsData) => {
            const { commonData, grid } = store$.value;
            const { campaignAndGroupSelector } = grid;
            const { login } = commonData;
            const { campaignSelected } = campaignAndGroupSelector;

            if (!campaignSelected) {
                return Observable.of();
            }

            const groupSuggestFilter = selectGroupsSuggestListFilter(store$.value, action.filter, campaignSelected);

            return Observable.fromPromise(deps.entitiesDataProvider.fetchGroupsSuggestList(login, groupSuggestFilter))
                .mergeMap((data: SelectorItems[]) => {
                    const preselectedGroup = selectPreselectedGroup(store$.value, data);

                    return Observable.concat(
                        Observable.of(updateCampaignAndGroupSelectorGroupsData(data)),
                        Observable.of(selectCampaignAndGroupSelectorGroup(preselectedGroup)),
                        Observable.of(changeCampaignsAndGroupSelectorStatus(SelectorStatus.READY))
                    );
                })
                .catch(() => Observable.of(changeCampaignsAndGroupSelectorStatus(SelectorStatus.GROUP_ERROR)));
        });

    const resetAction$ = action$
        .ofType(FETCH_CAMPAIGN_AND_GROUP_SELECTOR_GROUPS_DATA)
        .filter(() => {
            const {
                grid: { campaignAndGroupSelector }
            } = store$.value;
            const { selectorStatus } = campaignAndGroupSelector;

            return selectorStatus !== SelectorStatus.LOADING_GROUP;
        })
        .mergeMap((action: FetchCampaignAndGroupSelectorGroupsData) => {
            return Observable.of(updateCampaignAndGroupSelectorGroupsData([])).merge([
                changeCampaignsAndGroupSelectorStatus(SelectorStatus.LOADING_GROUP),
                selectCampaignAndGroupSelectorGroup(undefined)
            ]);
        });

    return resetAction$.merge(requestAction$);
};

const acceptCampaignAndGroupSelectorDataDnaEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(ACCEPT_CAMPAIGN_AND_GROUP_SELECTOR_DATA_DNA),
        mergeMap(
            (): Observable<
                | CreateMcBannerAdGroupAction
                | CreateTextAdGroupAction
                | GoToGroupsEditForCreationAction
                | GoToBannersEditForCreationAction
                | GoToInternalBannersEditForCreationAction
            > => {
                const {
                    grid: {
                        campaignAndGroupSelector: { campaignSelected, groupSelected, entityToSelectType }
                    },
                    commonData: {
                        client: { login },
                        operatorFeatures,
                        accessRights
                    }
                } = store$.value;

                if (login === undefined) {
                    throw new Error('client login is undefined');
                }

                if (!campaignSelected) {
                    return EMPTY;
                }

                switch (entityToSelectType) {
                    case CampaignAndGroupSelectorEntityToSelectType.GROUP:
                        if (selectAddableGroupTypes(operatorFeatures, accessRights).includes(campaignSelected.type)) {
                            switch (campaignSelected.type) {
                                case CampaignType.TEXT:
                                    const features = selectClientFeatures(store$.value);

                                    if (features.isHonestAddTextAdGroupAllowed) {
                                        return of(goToGroupsEditForCreation(login, campaignSelected.id));
                                    }
                                    // todo удалить после открытия фичи на 100% DIRECT-109443
                                    return of(createTextAdGroup(campaignSelected.id, getNewGroupName()));

                                case CampaignType.MCBANNER:
                                    return of(createMcBannerAdGroup(campaignSelected.id, getNewGroupName()));
                                case CampaignType.INTERNAL_FREE:
                                case CampaignType.INTERNAL_DISTRIB:
                                case CampaignType.INTERNAL_AUTOBUDGET:
                                case CampaignType.CPM_BANNER:
                                case CampaignType.CONTENT_PROMOTION:
                                case CampaignType.PERFORMANCE:
                                case CampaignType.CPM_PRICE: {
                                    return of(goToGroupsEditForCreation(login, campaignSelected.id));
                                }
                                default:
                                    return EMPTY;
                            }
                        } else {
                            throw new Error(`создание групп типа ${campaignSelected.type} не поддерживается`);
                        }
                    case CampaignAndGroupSelectorEntityToSelectType.BANNER:
                        if (
                            selectCampaignTypesForBannerCreation(operatorFeatures, accessRights).includes(
                                campaignSelected.type
                            )
                        ) {
                            if (!groupSelected) {
                                return EMPTY;
                            } else {
                                switch (campaignSelected.type) {
                                    case CampaignType.INTERNAL_FREE:
                                    case CampaignType.INTERNAL_DISTRIB:
                                    case CampaignType.INTERNAL_AUTOBUDGET:
                                        return Observable.of(
                                            goToInternalBannersEditForCreation(
                                                login,
                                                campaignSelected.id,
                                                groupSelected.id
                                            )
                                        );
                                    case CampaignType.TEXT:
                                    case CampaignType.CONTENT_PROMOTION:
                                    case CampaignType.PERFORMANCE:
                                    case CampaignType.CPM_BANNER:
                                    case CampaignType.MCBANNER:
                                        return of(
                                            goToBannersEditForCreation(login, campaignSelected.id, groupSelected.id)
                                        );
                                    default:
                                        return EMPTY;
                                }
                            }
                        } else {
                            throw new Error(
                                `создание баннеров для кампаний типа ${campaignSelected.type} не поддерживается`
                            );
                        }
                    default:
                        return EMPTY;
                }
            }
        )
    );
};

const acceptCampaignAndGroupSelectorDataEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(ACCEPT_CAMPAIGN_AND_GROUP_SELECTOR_DATA),
        mergeMap(
            (): DnaEpicAction => {
                const {
                    commonData: {
                        client: { login },
                        operatorFeatures: { isOldPhraseSelectorWizardDisabled }
                    },
                    grid: {
                        campaignAndGroupSelector: { campaignSelected, groupSelected, entityToSelectType }
                    }
                } = store$.value;

                if (login === undefined) {
                    throw new Error('client login is undefined');
                }

                if (!campaignSelected) {
                    return EMPTY;
                }

                switch (entityToSelectType) {
                    case CampaignAndGroupSelectorEntityToSelectType.GROUP:
                        return of(
                            addEntity({
                                type: GridEditedEntityType.Group,
                                campaignId: campaignSelected.id,
                                campaignType: campaignSelected.type,
                                login
                            })
                        );
                        break;
                    case CampaignAndGroupSelectorEntityToSelectType.BANNER:
                        if (groupSelected) {
                            return of(
                                addEntity({
                                    type: GridEditedEntityType.Banner,
                                    campaignId: campaignSelected.id,
                                    campaignType: campaignSelected.type,
                                    groupId: groupSelected ? groupSelected.id : '',
                                    login
                                })
                            );
                        }
                        break;
                    case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
                        if (groupSelected) {
                            if (isOldPhraseSelectorWizardDisabled) {
                                return of(
                                    phrasesSelectorOpenEditor(PhrasesSelectorWizardId.sideBar, {
                                        id: groupSelected ? groupSelected.id : '',
                                        groupType: groupSelected.groupType || GroupType.TEXT,
                                        name: groupSelected.name,
                                        campaign: {
                                            type: campaignSelected.type,
                                            name: campaignSelected.name,
                                            id: campaignSelected.id,
                                            strategy: {
                                                isAutoBudget: Boolean(campaignSelected.isAutoBudget)
                                            }
                                        },
                                        minusKeywords: groupSelected.minusKeywords || [],
                                        keywords: {},
                                        regionIds: groupSelected.regionsIds || []
                                    }),
                                    phrasesSelectorGetKeywords(
                                        PhrasesSelectorWizardId.sideBar,
                                        campaignSelected.id,
                                        groupSelected.id
                                    )
                                );
                            }
                            return of(
                                openPhrasesSelectorWizard(PhrasesSelectorWizardId.sideBar, {
                                    id: groupSelected ? groupSelected.id : '',
                                    groupType: groupSelected.groupType || GroupType.TEXT,
                                    name: groupSelected.name,
                                    campaign: {
                                        type: campaignSelected.type,
                                        name: campaignSelected.name,
                                        id: campaignSelected.id,
                                        strategy: {
                                            isAutoBudget: Boolean(campaignSelected.isAutoBudget)
                                        }
                                    },
                                    minusKeywords: groupSelected.minusKeywords || [],
                                    regionIds: groupSelected.regionsIds || []
                                })
                            );
                        }
                        break;
                    case CampaignAndGroupSelectorEntityToSelectType.PHRASE_BETA:
                        if (groupSelected) {
                            return of(
                                phrasesSelectorOpenEditor(PhrasesSelectorWizardId.sideBar, {
                                    id: groupSelected ? groupSelected.id : '',
                                    groupType: groupSelected.groupType || GroupType.TEXT,
                                    name: groupSelected.name,
                                    campaign: {
                                        type: campaignSelected.type,
                                        name: campaignSelected.name,
                                        id: campaignSelected.id,
                                        strategy: {
                                            isAutoBudget: Boolean(campaignSelected.isAutoBudget)
                                        }
                                    },
                                    minusKeywords: groupSelected.minusKeywords || [],
                                    keywords: {},
                                    regionIds: groupSelected.regionsIds || []
                                }),
                                phrasesSelectorGetKeywords(
                                    PhrasesSelectorWizardId.sideBar,
                                    campaignSelected.id,
                                    groupSelected.id
                                )
                            );
                        }
                        break;
                    default:
                        throw new Error('groupSelected not choosed or view type not supported');
                }

                return EMPTY;
            }
        )
    );
};

const openCampaignAndGroupSelectorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_CAMPAIGN_AND_GROUP_SELECTOR),
        mergeMap((action: OpenCampaignAndGroupSelector) => {
            return [fetchCampaignAndGroupSelectorCampaignsData('', action.entityToSelectType), closeSidebarMenus()];
        })
    );
};

const selectCampaignAndGroupSelectorCampaignEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(SELECT_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGN),
        mergeMap((action: SelectCampaignAndGroupSelectorCampaign) => {
            if (!action.campaignSelected) {
                return EMPTY;
            }

            const entityToSelectType = store$.value.grid.campaignAndGroupSelector.entityToSelectType;

            switch (entityToSelectType) {
                case CampaignAndGroupSelectorEntityToSelectType.BANNER:
                case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
                case CampaignAndGroupSelectorEntityToSelectType.PHRASE_BETA:
                    return of(fetchCampaignAndGroupSelectorGroupsData(''));
                default:
                    return EMPTY;
            }
        })
    );
};

const startAddEntityFromMenuEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(START_ADD_ENTITY_FROM_MENU),
        map(
            (action: StartAddEntityFromMenu): Action => {
                const { item: menuItem } = action;

                if (menuItem === CampaignAndGroupSelectorEntityToSelectType.CAMPAIGN) {
                    return openCampaignTypesMenu();
                } else {
                    return openCampaignAndGroupSelector(menuItem);
                }
            }
        )
    );
};

export {
    fetchCampaignAndGroupSelectorCampaignsDataEpic,
    fetchCampaignAndGroupSelectorGroupsDataEpic,
    acceptCampaignAndGroupSelectorDataEpic,
    openCampaignAndGroupSelectorEpic,
    selectCampaignAndGroupSelectorCampaignEpic,
    acceptCampaignAndGroupSelectorDataDnaEpic,
    startAddEntityFromMenuEpic
};
